import React, { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Typography, Select, Spinner } from '@telavita-core/react-design-kit'
import { CheckBox } from 'components'

import ModalGeneric from 'containers/ModalManager/Modals/ModalGeneric'
import { ReactComponent as WhatsappIcon} from 'static/svg/whatsapp.svg'
import { ReactComponent as DangerSvg } from 'static/svg/alert-icon.svg'
import { CANCELLATION_CONSTANTS, MODAL_TEXTS } from '../constants'
import { cancelScheduleService } from '../../../../services/cancelScheduleService'
import { isCancellationLimitReachedError } from 'utils/isCancellationLimitReachedError'
import { PROFESSIONAL_CANCELLATION_THRESHOLD } from 'settings/_reactQueryConstants'
import { parsePhoneNumber } from 'react-phone-number-input'
import { useCancelLimit } from './hooks/useCancelLimit'

import * as S from './styles'

const TALLY_FORM_URL = 'https://tally.so/r/3lNVpV'

interface CancelAppointmentModalProps {
  onClose: () => void
  scheduleId: string
  patientName: string
  patientPhone: string
  appointmentDate: string
  appointmentTime: string
  professionalUsername: string
  onReloadEvents: () => void
  onSuccess: () => void
}

export const CANCELATION_SCHEDULE_REASONS_OPTIONS = [
  {
    label: 'Alta do paciente',
    id: 'PATIENT_DISCHARGE'
  },
  {
    label: 'Cancelamento de reserva',
    id: 'RESERVATION_CANCELLATION'
  },
  {
    label: 'Compromisso profissional ou educacional (reuniões, cursos, aulas, etc)',
    id: 'PROFESSIONAL_OR_EDUCATIONAL_APPOINTMENT'
  },
  {
    label: 'Divergência do plano de atendimento',
    id: 'CARE_PLAN_DIVERGENCE'
  },
  {
    label: 'Faltas consecutivas ou desistência do atendimento',
    id: 'CONSECUTIVE_NO_SHOWS_OR_WITHDRAWAL'
  },
  {
    label: 'Férias e Recessos',
    id: 'VACATION_OR_RECESS'
  },
  {
    label: 'Imprevistos',
    id: 'UNEXPECTED_EVENTS'
  },
  {
    label: 'Licença Médica e outros motivos de saúde',
    id: 'MEDICAL_LEAVE_OR_HEALTH_REASONS'
  },
  {
    label: 'Mudança de horário',
    id: 'SCHEDULE_CHANGE'
  },
  {
    label: 'Problemas com internet, eletricidade, celular e computador',
    id: 'TECHNICAL_INFRASTRUCTURE_ISSUES'
  },
  {
    label: 'Outros',
    id: 'OTHER_REASONS'
  }
]

const MODAL_STEPS = {
  CONFIRMATION: 'confirmation',
  REASON: 'reason',
  LIMIT_REACHED: 'limit_reached'
} as const

type ModalStep = typeof MODAL_STEPS[keyof typeof MODAL_STEPS]

export function CancelAppointmentModal({
  onClose,
  scheduleId,
  patientName,
  patientPhone,
  appointmentDate,
  appointmentTime,
  professionalUsername,
  onReloadEvents,
  onSuccess,
}: CancelAppointmentModalProps): JSX.Element {
  const [currentStep, setCurrentStep] = useState<ModalStep>(MODAL_STEPS.CONFIRMATION)
  const [hasContactedPatient, setHasContactedPatient] = useState(false)
  const [selectedReason, setSelectedReason] = useState<{id: number | string; label: string} | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()

  const {
    cancelLimit,
    isLoading: isLoadingCancelLimit,
    isLimitReached,
  } = useCancelLimit(professionalUsername)

  useEffect(() => {
    if (!isLoadingCancelLimit && isLimitReached) {
      setCurrentStep(MODAL_STEPS.LIMIT_REACHED)
    }
  }, [isLoadingCancelLimit, isLimitReached])

  const handleWhatsAppClick = () => {
    let formattedPhone = patientPhone.replace(/\D/g, '')
    if (!formattedPhone.startsWith(CANCELLATION_CONSTANTS.BRAZIL_COUNTRY_CODE)) {
      formattedPhone = CANCELLATION_CONSTANTS.BRAZIL_COUNTRY_CODE + formattedPhone
    }

    const whatsappUrl = `${CANCELLATION_CONSTANTS.WHATSAPP_BASE_URL}${formattedPhone}`
    window.open(whatsappUrl, '_blank')
  }

  const handleContinueToReason = () => {
    if (!hasContactedPatient || isLimitReached) return
    setCurrentStep(MODAL_STEPS.REASON)
  }

  const handleConfirmCancel = async () => {
    if (!selectedReason) return

    setIsLoading(true)
    try {
      await cancelScheduleService({
        scheduleId,
        reasonCode: selectedReason.id as string,
        requestedBy: professionalUsername
      })
      onReloadEvents?.()
      onSuccess()
    } catch (error) {
      if (isCancellationLimitReachedError(error)) {
        await queryClient.invalidateQueries({
          queryKey: [PROFESSIONAL_CANCELLATION_THRESHOLD, professionalUsername],
        })
        setCurrentStep(MODAL_STEPS.LIMIT_REACHED)
        return
      }

      console.error('Erro ao cancelar consulta:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const canContinueToReason = hasContactedPatient && !isLimitReached
  const canConfirmCancel = selectedReason && selectedReason.id && !isLoading
  const isLimitReachedStep = currentStep === MODAL_STEPS.LIMIT_REACHED
  const isAtLimit = cancelLimit ? cancelLimit.remaining <= 0 : false

  const renderCancelLimitPanel = () => {
    if (!cancelLimit) return null

    const { monthlyThreshold, cancellations, remaining } = cancelLimit

    return (
      <S.CancelLimitPanelWrapper>
        <S.CancelLimitPanel>
          <S.CancelLimitCounter isAtLimit={isAtLimit}>
            {cancellations}/{monthlyThreshold}
          </S.CancelLimitCounter>
          <S.CancelLimitDivider />
          <S.CancelLimitDetails>
            <S.CancelLimitRow>
              <Typography variant="content2">Limite mensal:</Typography>
              <Typography variant="content2" weight="bold">{monthlyThreshold}</Typography>
            </S.CancelLimitRow>
            <S.CancelLimitRow>
              <Typography variant="content2">Canceladas:</Typography>
              <Typography variant="content2" weight="bold">{cancellations}</Typography>
            </S.CancelLimitRow>
            <S.CancelLimitRow>
              <Typography variant="content2">Restam:</Typography>
              <Typography variant="content2" weight="bold">{remaining}</Typography>
            </S.CancelLimitRow>
          </S.CancelLimitDetails>
        </S.CancelLimitPanel>
      </S.CancelLimitPanelWrapper>
    )
  }

  const renderConfirmationStep = () => (
    <>
      {renderCancelLimitPanel()}
      <S.AppointmentTime>
        <Typography variant="content2" center color="danger">
          {appointmentDate} - {appointmentTime}
        </Typography>
      </S.AppointmentTime>
      <S.WarningSectionCentered>
        <Typography variant="content2" weight="bold" color="danger" center>
          Atenção:
        </Typography>
        <Typography variant="content2" color="danger" center>
          Antes de cancelar, entre em contato e comunique ao paciente:
        </Typography>
      </S.WarningSectionCentered>
      <S.PhoneNumber>
        <Typography weight="bold" color="danger" center>
          {patientPhone && parsePhoneNumber(`+${patientPhone}`).formatNational()}
        </Typography>
      </S.PhoneNumber>
      <S.WhatsAppSection>
        <S.WhatsAppButton onClick={handleWhatsAppClick}>
          <WhatsappIcon color='#1F35B5'/>
          <Typography variant="content2" color="primary" weight='bold'>
            Enviar mensagem por WhatsApp
          </Typography>
        </S.WhatsAppButton>
      </S.WhatsAppSection>
      <S.CheckboxSection>
        <CheckBox
          checkBoxId='contacted-patient'
          indeterminate={false}
          isSelected={hasContactedPatient}
          onClickCheckBox={(event) => setHasContactedPatient(event)}
        />
        <S.CheckboxLabel htmlFor="contacted-patient">
          <Typography variant="content2">
            <strong>Já entrei em contato</strong> com o paciente e estamos alinhados quanto ao cancelamento.
          </Typography>
        </S.CheckboxLabel>
      </S.CheckboxSection>
    </>
  )

  const renderReasonStep = () => (
    <>
      {renderCancelLimitPanel()}
      <S.AppointmentTime>
        <Typography variant="content2" color="danger">
          {appointmentDate} - {appointmentTime}
        </Typography>
      </S.AppointmentTime>
      <S.ReasonSection>
        <Select
          label={'Motivo do cancelamento*'}
          placeholder="Selecione um motivo"
          selected={selectedReason}
          onSelect={(value) => setSelectedReason(value)}
          options={CANCELATION_SCHEDULE_REASONS_OPTIONS}
        />
      </S.ReasonSection>
      <S.WarningSection>
        <div>
          <DangerSvg width={24} height={24}/>
        </div>
        <Typography variant="content2" color="danger">
          O cancelamento compromete o tratamento do paciente, portanto, utilize apenas em último caso. Excesso de cancelamentos podem resultar na rescisão do contrato.
        </Typography>
      </S.WarningSection>
      <S.RequiredField>
        <Typography variant="content2">
          <i>*Preenchimento obrigatório</i>
        </Typography>
      </S.RequiredField>
    </>
  )

  const renderLimitReachedStep = () => (
    <>
      {renderCancelLimitPanel()}
      <S.LimitReachedMessage>
        <S.WarningSection>
          <div>
            <DangerSvg width={24} height={24}/>
          </div>
          <S.LimitReachedTextContent>
            <Typography variant="content2" color="danger" weight="bold">
              Você atingiu o limite mensal de cancelamento de consultas.
            </Typography>
            <Typography variant="content2" color="danger">
              Só é possível fazer uma nova solicitação de cancelamento preenchendo um formulário, que será analisado por nossa equipe de gestão de profissionais.
            </Typography>
            <S.FormLink href={TALLY_FORM_URL} target="_blank" rel="noopener noreferrer">
              <Typography variant="content2" color="primary" weight="bold">
                Solicitar cancelamento pelo formulário
              </Typography>
            </S.FormLink>
          </S.LimitReachedTextContent>
        </S.WarningSection>
      </S.LimitReachedMessage>
    </>
  )

  const renderModalContent = () => {
    if (isLoadingCancelLimit) {
      return (
        <S.LoadingWrapper>
          <Spinner />
        </S.LoadingWrapper>
      )
    }

    if (currentStep === MODAL_STEPS.CONFIRMATION) return renderConfirmationStep()
    if (currentStep === MODAL_STEPS.REASON) return renderReasonStep()
    return renderLimitReachedStep()
  }

  const getButtonProps = () => {
    if (isLoadingCancelLimit) {
      return {
        hasButton: false,
        btnDisabled: true,
        btnLoading: false,
        btnText: MODAL_TEXTS.BUTTON_CONTINUE,
        btnOnClick: handleContinueToReason,
      }
    }

    if (isLimitReachedStep) {
      return {
        hasButton: true,
        btnDisabled: false,
        btnLoading: false,
        btnText: 'FECHAR',
        btnOnClick: onClose,
      }
    }

    if (currentStep === MODAL_STEPS.CONFIRMATION) {
      return {
        hasButton: true,
        btnDisabled: !canContinueToReason,
        btnLoading: false,
        btnText: MODAL_TEXTS.BUTTON_CONTINUE,
        btnOnClick: handleContinueToReason,
      }
    }

    return {
      hasButton: true,
      btnDisabled: !canConfirmCancel,
      btnLoading: isLoading,
      btnText: isLoading ? MODAL_TEXTS.BUTTON_CONFIRMING : MODAL_TEXTS.BUTTON_CONFIRM,
      btnOnClick: handleConfirmCancel,
    }
  }

  const buttonProps = getButtonProps()

  return (
    <ModalGeneric
      modalSubtitle={
        <S.SubTitleContent>
          <Typography center variant="content2">
            Paciente <strong>{patientName}</strong>
          </Typography>
        </S.SubTitleContent>
      }
      modalTitle={MODAL_TEXTS.APPOINTMENT_TITLE}
      hasCloseButton
      onCloseModal={onClose}
      {...buttonProps}
    >
      <S.ModalContent>
        {renderModalContent()}
      </S.ModalContent>
    </ModalGeneric>
  )
}
